import i18n from "@/i18n";
/**
 * WorkforceTeam — Prompt 10B §6-§18.
 *
 * Team list for the current active MiseOne Location, dated by the
 * BROWSER-LOCAL calendar day at the time of fetch. Includes an
 * `+ Add Employee` action that opens the right-side AddEmployeeSheet.
 * Rows are NOT clickable in Prompt 10B — Employee Profile lives in a
 * later block.
 *
 * Reused MiseOne primitives: PageHeader, Button, Input, Badge,
 * Skeleton, Sheet (via AddEmployeeSheet), sonner toast.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Search as SearchIcon } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useApp } from "@/context/AppContext";
import { canManageAbsences } from "@/components/workforce/absenceShared";
import { api } from "@/lib/api";
import AddEmployeeSheet from "@/components/workforce/AddEmployeeSheet";
import EmployeeProfileSheet from
    "@/components/workforce/EmployeeProfileSheet";
import EmployeeEditSheet from
    "@/components/workforce/EmployeeEditSheet";
import {
    EMPLOYEE_NUMBER_TAKEN_MESSAGE, isEmployeeNumberTaken,
} from "@/components/workforce/EmployeeNumberInput";
import {
    browserLocalYMD, contractTypeLabel, formatContractCell,
} from "@/components/workforce/workforceHelpers";

const COLUMNS = [
    { key: "employee", get label() { return i18n.t("wf.workforceTeam.employee", "Employee"); } },
    { key: "role", get label() { return i18n.t("common.role", "Role"); } },
    { key: "main_location", get label() { return i18n.t("wf.workforceTeam.mainLocation", "Main Location"); } },
    { key: "contract", get label() { return i18n.t("wf.workforceTeam.contract", "Contract"); } },
    { key: "status", get label() { return i18n.t("common.status", "Status"); } },
];

// Prompt 10B §8 — client-side, case-insensitive search across
// first_name, last_name, full name, main_role.
function matchesQuery(row, q) {
    if (!q) return true;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    const first = (row.first_name || "").toLowerCase();
    const last = (row.last_name || "").toLowerCase();
    const full = `${first} ${last}`.trim();
    const role = (row.main_role || "").toLowerCase();
    return first.includes(needle) || last.includes(needle) ||
        full.includes(needle) || role.includes(needle);
}

export default function WorkforceTeam() {
    const { restaurantId, currentRestaurant, user } = useApp();

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [query, setQuery] = useState("");
    const [sheetOpen, setSheetOpen] = useState(false);
    // Prompt 10C — currently selected Employee for the Profile sheet.
    const [profileEmployeeId, setProfileEmployeeId] = useState(null);
    const [profileOpen, setProfileOpen] = useState(false);
    // Prompt 12L-2C — Edit sheet state. `editEmployee` holds the
    // canonical snapshot used to seed initial values; PATCH is owned
    // by this page.
    const [editEmployee, setEditEmployee] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [editPending, setEditPending] = useState(false);
    // Bumps every canonical reload so the Profile sheet's memoized
    // fetch effect can be forced to re-run after a successful PATCH
    // without permanently optimistic-patching local state
    // (Prompt 12L-2C §12).
    const [profileReloadKey, setProfileReloadKey] = useState(0);

    const restaurantName = currentRestaurant?.name || "";

    const fetchTeam = useCallback(async () => {
        if (!restaurantId) {
            setRows([]);
            setLoading(false);
            setError(false);
            return;
        }
        setLoading(true);
        setError(false);
        // Prompt 10B §15 — do not display stale previous-Location rows.
        setRows([]);
        try {
            const { data } = await api.get("/workforce/team", {
                params: {
                    restaurant_id: restaurantId,
                    as_of: browserLocalYMD(),
                },
            });
            setRows(Array.isArray(data?.employees) ? data.employees : []);
        } catch (_err) {
            setError(true);
        } finally {
            setLoading(false);
        }
    }, [restaurantId]);

    // Prompt 10B §5 · §37 · Prompt 10C §29 — Location change refetches
    // Team AND closes both the Add Employee panel and the Employee
    // Profile sheet, clearing the selected Employee. Prompt 12L-2C §15
    // — also close any pending Edit flow so a Location change does
    // not leak a stale employee snapshot into another Location.
    useEffect(() => {
        setSheetOpen(false);
        setProfileOpen(false);
        setProfileEmployeeId(null);
        setEditOpen(false);
        setEditEmployee(null);
        setEditPending(false);
        setQuery("");
        fetchTeam();
    }, [restaurantId, fetchTeam]);

    const filtered = useMemo(
        () => rows.filter((r) => matchesQuery(r, query)),
        [rows, query],
    );

    const showEmpty = !loading && !error && rows.length === 0;
    const showSearchEmpty = !loading && !error && rows.length > 0 &&
                                     filtered.length === 0;

    const addButton = (
        <Button
            data-testid="workforce-team-add-employee"
            onClick={() => setSheetOpen(true)}
        >
            <Plus className="h-4 w-4" strokeWidth={2} />
            {i18n.t("wf.workforceTeam.addEmployee", "Add Employee")}
        </Button>
    );

    return (
        <div data-testid="workforce-team-page">
            <PageHeader
                title={i18n.t("wf.workforceTeam.team", "Team")}
                testId="workforce-team-header"
                action={canManageAbsences(user) ? addButton : null}
            />

            <div
                data-testid="workforce-team-content"
                className="px-4 sm:px-6 pb-8 space-y-4"
            >
                {/* Search */}
                <div className="relative max-w-sm">
                    <SearchIcon
                        className="pointer-events-none absolute left-2
                                    top-1/2 -translate-y-1/2 h-4 w-4
                                    text-neutral-400"
                        strokeWidth={2}
                    />
                    <Input
                        data-testid="workforce-team-search"
                        className="pl-8"
                        placeholder={i18n.t("wf.workforceTeam.searchEmployees", "Search employees")}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                    />
                </div>

                {error && (
                    <div
                        data-testid="workforce-team-error"
                        className="rounded-md border border-neutral-200
                                    bg-white p-6 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.workforceTeam.couldNotLoadTheTeam", "Could not load the team.")}
                        </p>
                        <Button
                            variant="outline"
                            onClick={fetchTeam}
                            data-testid="workforce-team-retry"
                        >
                            {i18n.t("wf.workforceTeam.retry", "Retry")}
                        </Button>
                    </div>
                )}

                {loading && !error && (
                    <div
                        data-testid="workforce-team-loading"
                        className="space-y-2"
                    >
                        {[0, 1, 2, 3, 4].map((i) => (
                            <Skeleton
                                key={i}
                                className="h-10 w-full"
                                data-testid={`workforce-team-skeleton-${i}`}
                            />
                        ))}
                    </div>
                )}

                {showEmpty && (
                    <div
                        data-testid="workforce-team-empty"
                        className="rounded-md border border-neutral-200
                                    bg-white p-8 text-center space-y-3"
                    >
                        <p className="text-sm text-neutral-700">
                            {i18n.t("wf.workforceTeam.noEmployeesInThisLocationYet", "No employees in this location yet.")}
                        </p>
                        <Button
                            onClick={() => setSheetOpen(true)}
                            data-testid="workforce-team-empty-add"
                        >
                            <Plus className="h-4 w-4" strokeWidth={2} />
                            {i18n.t("wf.workforceTeam.addEmployee", "Add Employee")}
                        </Button>
                    </div>
                )}

                {showSearchEmpty && (
                    <div
                        data-testid="workforce-team-search-empty"
                        className="rounded-md border border-neutral-200
                                    bg-white p-6 text-center text-sm
                                    text-neutral-700"
                    >
                        {i18n.t("wf.workforceTeam.noEmployeesMatchYourSearch", "No employees match your search.")}
                    </div>
                )}

                {!loading && !error && filtered.length > 0 && (
                    <div
                        data-testid="workforce-team-table-wrap"
                        className="overflow-x-auto rounded-md border
                                    border-neutral-200 bg-white"
                    >
                        <table
                            data-testid="workforce-team-table"
                            className="w-full text-sm"
                        >
                            <thead className="bg-neutral-50 text-neutral-500">
                                <tr>
                                    {COLUMNS.map((c) => (
                                        <th
                                            key={c.key}
                                            data-testid={`workforce-team-col-${c.key}`}
                                            className="text-left font-medium
                                                        px-4 py-2 border-b
                                                        border-neutral-200"
                                        >
                                            {c.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((row) => (
                                    <tr
                                        key={row.employee_id}
                                        data-testid={`workforce-team-row-${row.employee_id}`}
                                        className="border-b border-neutral-100
                                                    last:border-0"
                                    >
                                        <td className="px-4 py-3">
                                            <button
                                                type="button"
                                                data-testid={`workforce-team-row-open-${row.employee_id}`}
                                                onClick={() => {
                                                    setProfileEmployeeId(
                                                        row.employee_id);
                                                    setProfileOpen(true);
                                                }}
                                                className="text-neutral-900
                                                            hover:underline
                                                            focus:outline-none
                                                            focus-visible:underline"
                                            >
                                                {row.first_name}{" "}
                                                {row.last_name}
                                                {row.worker_type
                                                    === "EXTRA" && (
                                                    <span
                                                        data-testid="workforce-team-row-extra-badge"
                                                        className="ml-2
                                                            inline-flex
                                                            items-center
                                                            rounded-full
                                                            border
                                                            border-neutral-300
                                                            bg-neutral-100
                                                            px-2 py-0.5
                                                            text-[10px]
                                                            font-medium
                                                            uppercase
                                                            tracking-wide
                                                            text-neutral-700"
                                                    >
                                                        {i18n.t("wf.workforceTeam.extra", "Extra")}
                                                    </span>
                                                )}
                                            </button>
                                            {row.employee_number && (
                                                <div
                                                    data-testid={`workforce-team-row-number-${row.employee_id}`}
                                                    className="text-xs
                                                        text-neutral-500
                                                        tabular-nums"
                                                >
                                                    {i18n.t("wf.workforceTeam.employeeNumber", "Employee #{{number}}", { number: row.employee_number })}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            {row.main_role}
                                        </td>
                                        <td className="px-4 py-3">
                                            {restaurantName}
                                        </td>
                                        <td className="px-4 py-3">
                                            {formatContractCell(row)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge
                                                variant="secondary"
                                                data-testid="workforce-team-row-status"
                                            >
                                                {i18n.t("common.active", "Active")}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <AddEmployeeSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                restaurantId={restaurantId}
                restaurantName={restaurantName}
                onCreated={fetchTeam}
            />

            <EmployeeProfileSheet
                open={profileOpen}
                reloadKey={profileReloadKey}
                onOpenChange={(v) => {
                    setProfileOpen(v);
                    // Prompt 10C §33 — closing clears selection so a
                    // subsequent reopen does not surface stale data.
                    if (!v) setProfileEmployeeId(null);
                }}
                employeeId={profileEmployeeId}
                onEdit={(emp) => {
                    // Prompt 12L-2C §5 — open Edit sheet for the
                    // currently selected canonical Employee.
                    setEditEmployee(emp);
                    setEditOpen(true);
                }}
            />

            <EmployeeEditSheet
                open={editOpen}
                onOpenChange={(v) => {
                    // Prompt 12L-2C §11 — never close mid-flight.
                    if (editPending) return;
                    setEditOpen(v);
                    if (!v) setEditEmployee(null);
                }}
                employee={editEmployee}
                pending={editPending}
                onSave={async (body) => {
                    // Prompt 12L-2C §10 — PATCH is owned here. Query
                    // uses the active Team `restaurantId`; body
                    // contains ONLY the fields that actually changed.
                    if (editPending || !editEmployee || !restaurantId) {
                        return;
                    }
                    setEditPending(true);
                    try {
                        await api.patch(
                            `/workforce/employees/${editEmployee.employee_id}`,
                            body,
                            { params: { restaurant_id: restaurantId } });
                        // Prompt 12L-2C §12 — close edit state,
                        // canonical Team reload, canonical profile
                        // reload via reloadKey bump. No permanent
                        // optimistic patch.
                        setEditOpen(false);
                        setEditEmployee(null);
                        setProfileReloadKey((k) => k + 1);
                        fetchTeam();
                        toast.success(i18n.t("wf.workforceTeam.employeeUpdated", "Employee updated"));
                    } catch (err) {
                        // Prompt 12L-2C §14 — keep sheet open for
                        // 422; do not destroy profile for other
                        // errors. No automatic retry.
                        const status = err?.response?.status;
                        if (isEmployeeNumberTaken(err)) {
                            toast.error(i18n.t("wf.EmployeeNumberInput.EMPLOYEE_NUMBER_TAKEN_MESSAGE", EMPLOYEE_NUMBER_TAKEN_MESSAGE));
                        } else if (status === 422) {
                            toast.error(
                                i18n.t("wf.workforceTeam.checkEmployeeInfo", "Check the employee information and try again."));
                        } else if (status === 403) {
                            toast.error(
                                i18n.t("wf.workforceTeam.cannotEditEmployee", "You cannot edit this employee."));
                        } else if (status === 404) {
                            toast.error(
                                i18n.t("wf.workforceTeam.employeeNotFound", "This employee could not be found."));
                        } else {
                            toast.error(
                                i18n.t("wf.workforceTeam.couldNotUpdateEmployee", "Could not update employee. Try again."));
                        }
                    } finally {
                        setEditPending(false);
                    }
                }}
            />
        </div>
    );
}

// Test-only exports (pure helpers).
export { matchesQuery, contractTypeLabel };
